/**
 * ConversationList Component
 *
 * Sidebar component showing all conversations sorted by last message time.
 * Features search/filter, online indicators, archive/muted filters.
 */

import React, { useState, useMemo } from 'react';
import {
  Search as SearchIcon,
  Archive,
  MessageSquare,
  Filter,
  Plus,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ConversationItem } from './ConversationItem';
import type { Conversation } from '@/types/messaging';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  isLoading: boolean;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
  onSearch?: () => void;
}

type FilterType = 'all' | 'unread' | 'archived';

/**
 * Loading skeleton for conversation list
 */
const ConversationListSkeleton: React.FC = () => (
  <div className="space-y-2 p-3">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-3 p-3">
        <Skeleton className="h-12 w-12 rounded-full shrink-0" />
        <div className="flex-1">
          <Skeleton className="h-4 w-28 mb-2" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    ))}
  </div>
);

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  isLoading,
  onSelectConversation,
  onNewConversation,
  onSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');

  /**
   * Filter and sort conversations
   */
  const filteredConversations = useMemo(() => {
    let result = [...conversations];

    // Apply filter
    switch (filter) {
      case 'unread':
        result = result.filter((c) => c.unreadCount > 0);
        break;
      case 'archived':
        result = result.filter((c) => !!c.archivedAt);
        break;
      case 'all':
      default:
        result = result.filter((c) => !c.archivedAt);
        break;
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((c) => {
        // For group conversations, search by group name
        if (c.isGroup) {
          const groupName = (c.groupName || '').toLowerCase();
          return groupName.includes(query);
        }
        // For direct conversations, search by participant name
        if (c.participant) {
          const name = `${c.participant.firstName || ''} ${c.participant.lastName || ''}`.toLowerCase();
          const username = (c.participant.username || '').toLowerCase();
          return name.includes(query) || username.includes(query);
        }
        return false;
      });
    }

    // Sort by last message time (newest first)
    result.sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bTime - aTime;
    });

    return result;
  }, [conversations, filter, searchQuery]);

  // Count stats
  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);
  const archivedCount = conversations.filter((c) => c.archivedAt).length;

  return (
    <div className="h-full flex flex-col bg-background border-r">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Messages
            {totalUnread > 0 && (
              <Badge
                className="h-5 min-w-[20px] px-1.5 text-xs"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                }}
              >
                {totalUnread}
              </Badge>
            )}
          </h2>

          <div className="flex items-center gap-2">
            {onSearch && (
              <Button
                onClick={onSearch}
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0"
              >
                <SearchIcon className="h-4 w-4" />
                <span className="sr-only">Search messages</span>
              </Button>
            )}
            <Button
              onClick={onNewConversation}
              size="sm"
              style={{
                backgroundColor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          </div>
        </div>

        {/* Search input */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {/* Filter dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
              >
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="sr-only">Filter conversations</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-muted' : ''}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                All Messages
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setFilter('unread')}
                className={filter === 'unread' ? 'bg-muted' : ''}
              >
                <Badge className="h-4 w-4 mr-2 p-0 flex items-center justify-center text-[10px]">
                  {totalUnread}
                </Badge>
                Unread Only
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setFilter('archived')}
                className={filter === 'archived' ? 'bg-muted' : ''}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archived ({archivedCount})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Conversation list */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <ConversationListSkeleton />
        ) : filteredConversations.length === 0 ? (
          <div className="p-6 text-center">
            <div
              className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--primary) / 0.1)' }}
            >
              <MessageSquare className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
            </div>
            <p className="text-sm font-medium mb-1">
              {searchQuery
                ? 'No conversations found'
                : filter === 'unread'
                ? 'No unread messages'
                : filter === 'archived'
                ? 'No archived conversations'
                : 'No conversations yet'}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {searchQuery
                ? 'Try a different search term'
                : filter !== 'all'
                ? 'Change filter to see more'
                : 'Start a conversation with a connection'}
            </p>
            {!searchQuery && filter === 'all' && (
              <Button
                onClick={onNewConversation}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Message
              </Button>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isActive={conversation.id === activeConversationId}
                onClick={() => onSelectConversation(conversation.id)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default ConversationList;
